import { Box, BoxProps } from "@chakra-ui/react"
import classnames from "classnames"
import { useEffect, useState } from "react"
import {
  DiscussionContextInput,
  DiscussionPostFragment
} from "@pathwright/graphql-client/src/generated"
import DiscussionForm from "../../form/DiscussionForm"
import DiscussionListItem from "./DiscussionListItem"

type Mode = "create" | "edit" | "view"

type DiscussionItemContainerProps = {
  discussion?: DiscussionPostFragment | null
  context: DiscussionContextInput
  className?: string
  isDiscussionQuestion?: boolean
  compact?: boolean
  showReplyPrompt?: boolean
  initialMode: Mode
  onCreateDiscussion?: (discussion: DiscussionPostFragment) => void
  onDeleteDiscussion?: (discussion: DiscussionPostFragment) => void
  onCancel?: () => void
} & BoxProps

const DiscussionItemContainer = ({
  discussion,
  context,
  className: classNameProp,
  isDiscussionQuestion,
  compact = true,
  showReplyPrompt = false,
  initialMode,
  onCreateDiscussion,
  onDeleteDiscussion,
  onCancel,
  ...boxProps
}: DiscussionItemContainerProps) => {
  const [mode, setMode] = useState<Mode>(initialMode)

  useEffect(() => {
    if (!discussion) {
      // Creating new discussion.
      setMode("create")
    } else {
      // If discussion now exists when in create mode, we switch to view mode.
      if (mode === "create") {
        setMode("view")
      }
    }
  }, [discussion, mode])

  const className = classnames("DiscussionItemContainer", classNameProp, {
    [`DiscussionItemContainer__${mode}`]: !!mode
  })

  switch (mode) {
    case "create":
      return (
        <Box className={className} {...boxProps}>
          <DiscussionForm
            context={context}
            isDiscussionQuestion={isDiscussionQuestion}
            onSubmit={onCreateDiscussion}
            onCancel={onCancel}
            inverted={false}
            expanded
          />
        </Box>
      )
    case "edit":
      return (
        <Box className={className} {...boxProps}>
          <DiscussionForm
            context={context}
            discussion={discussion}
            onCancel={() => setMode("view")}
            onSubmit={() => setMode("view")}
            inverted={false}
            expanded
          />
        </Box>
      )
    case "view":
      return (
        <Box className={className} {...boxProps}>
          <DiscussionListItem
            discussion={discussion}
            onEditDiscussion={() => setMode("edit")}
            onDeleteDiscussion={onDeleteDiscussion}
            compact={compact}
            showReplyPrompt={showReplyPrompt}
          />
        </Box>
      )
    default:
      return null
  }
}

DiscussionItemContainer.displayName = "DiscussionItemContainer"

export default DiscussionItemContainer
